import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   ProductsManager.js — Full product + inventory management
   Sidebar: same pattern as Profile.js / UserDashboard.js
   Tabs: Products · Units · Bulk Import
   Admin + Superadmin only
───────────────────────────────────────────────────────────────────────────── */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root{
  --bg:#070b14;--bg2:#0d1525;--card:#111e33;
  --blue:#38bdf8;--blue2:#0ea5e9;--green:#34d399;
  --amber:#fbbf24;--red:#f87171;--purple:#a78bfa;
  --text:#e2e8f0;--muted:#4a6080;--dim:#1a2840;
  --border:rgba(255,255,255,0.07);--gold:#f5a623;
}
*{box-sizing:border-box;margin:0;padding:0;}
.pm-root{display:flex;min-height:100vh;background:var(--bg);color:var(--text);font-family:'DM Sans',sans-serif;}
/* SIDEBAR */
.pm-sidebar{width:220px;flex-shrink:0;height:100vh;position:sticky;top:0;background:rgba(5,9,20,0.98);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow-y:auto;z-index:20;transition:width 0.3s;}
.pm-sidebar.col{width:54px;}
@media(max-width:800px){.pm-sidebar{position:fixed;left:0;top:0;height:100vh;z-index:100;transform:translateX(0);transition:transform 0.3s;}.pm-sidebar.hidden{transform:translateX(-100%);}}
.pm-sb-head{display:flex;align-items:center;justify-content:space-between;padding:14px 12px;border-bottom:1px solid var(--border);flex-shrink:0;}
.pm-logo{font-family:'Syne',sans-serif;font-size:1.25rem;font-weight:800;color:var(--text);text-decoration:none;}
.pm-logo span{color:var(--gold);}
.pm-col-btn{width:26px;height:26px;border-radius:7px;background:rgba(255,255,255,0.05);border:1px solid var(--border);color:var(--muted);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;transition:all 0.2s;}
.pm-col-btn:hover{background:rgba(56,189,248,0.12);color:var(--blue);}
.pm-nav-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:9px;margin:2px 8px;cursor:pointer;border:none;background:transparent;width:calc(100% - 16px);color:var(--muted);font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;text-align:left;transition:all 0.16s;white-space:nowrap;}
.pm-nav-item:hover{background:rgba(56,189,248,0.08);color:var(--text);}
.pm-nav-item.active{background:rgba(56,189,248,0.14);color:var(--blue);outline:1px solid rgba(56,189,248,0.22);}
.pm-nav-icon{font-size:15px;flex-shrink:0;width:20px;text-align:center;}
.pm-nav-label{flex:1;overflow:hidden;text-overflow:ellipsis;}
.pm-sidebar.col .pm-nav-label,.pm-sidebar.col .pm-logo-text{display:none;}
.pm-sidebar.col .pm-nav-item{justify-content:center;padding:10px;width:calc(100% - 8px);margin:2px 4px;}
.pm-sb-section{padding:8px 0;}
.pm-sb-sec-title{font-size:9px;font-weight:700;color:rgba(255,255,255,0.18);text-transform:uppercase;letter-spacing:2px;padding:5px 18px 3px;display:block;}
.pm-sidebar.col .pm-sb-sec-title{display:none;}
.pm-sb-bot{margin-top:auto;padding:10px 8px;border-top:1px solid var(--border);flex-shrink:0;}
.pm-logout{display:flex;align-items:center;gap:9px;width:100%;padding:9px 10px;border-radius:9px;background:rgba(248,113,113,0.06);border:1px solid rgba(248,113,113,0.15);color:var(--red);font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.16s;}
.pm-logout:hover{background:rgba(248,113,113,0.12);}
.pm-sidebar.col .pm-logout{justify-content:center;}
.pm-sidebar.col .pm-logout-lbl{display:none;}
/* MAIN */
.pm-main{flex:1;min-width:0;display:flex;flex-direction:column;}
.pm-topbar{display:none;align-items:center;justify-content:space-between;padding:0 16px;height:52px;background:rgba(5,9,20,0.98);border-bottom:1px solid var(--border);flex-shrink:0;}
@media(max-width:800px){.pm-topbar{display:flex;}}
.pm-menu-btn{width:32px;height:32px;border-radius:7px;background:rgba(255,255,255,0.05);border:1px solid var(--border);color:var(--text);cursor:pointer;font-size:17px;display:flex;align-items:center;justify-content:center;}
.pm-header{padding:22px 24px 0;display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;flex-shrink:0;}
.pm-title{font-family:'Syne',sans-serif;font-size:1.4rem;font-weight:800;color:var(--text);}
.pm-sub{font-size:11px;color:var(--muted);font-family:'DM Mono',monospace;margin-top:2px;}
.pm-tabs{display:flex;gap:2px;padding:16px 24px 0;border-bottom:1px solid var(--border);flex-shrink:0;}
.pm-tab{padding:9px 16px;border:none;background:transparent;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;color:var(--muted);border-bottom:2px solid transparent;border-radius:6px 6px 0 0;transition:all 0.16s;white-space:nowrap;}
.pm-tab:hover{color:var(--text);background:rgba(255,255,255,0.03);}
.pm-tab.active{color:var(--blue);border-bottom-color:var(--blue);background:rgba(56,189,248,0.08);}
.pm-content{flex:1;overflow-y:auto;padding:22px 24px 40px;}
@media(max-width:600px){.pm-content{padding:16px;}}
/* Cards */
.pm-card{background:var(--card);border:1px solid var(--border);border-radius:16px;overflow:hidden;}
.pm-input{width:100%;padding:9px 13px;background:rgba(255,255,255,0.05);border:1px solid rgba(56,189,248,0.18);border-radius:9px;color:var(--text);font-family:'DM Sans',sans-serif;font-size:13px;outline:none;transition:border 0.2s;}
.pm-input:focus{border-color:var(--blue);}
.pm-select{width:100%;padding:9px 13px;background:rgba(13,21,37,0.9);border:1px solid rgba(56,189,248,0.18);border-radius:9px;color:var(--text);font-family:'DM Sans',sans-serif;font-size:13px;outline:none;cursor:pointer;}
.pm-select option{background:#0d1525;}
.pm-btn-primary{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:9px;border:none;background:linear-gradient(135deg,var(--blue),var(--blue2));color:#000;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.18s;}
.pm-btn-primary:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(56,189,248,0.3);}
.pm-btn-ghost{display:inline-flex;align-items:center;gap:7px;padding:8px 15px;border-radius:9px;border:1px solid var(--border);background:transparent;color:var(--muted);font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;cursor:pointer;transition:all 0.16s;}
.pm-btn-ghost:hover{background:rgba(255,255,255,0.05);color:var(--text);}
.pm-btn-danger{display:inline-flex;align-items:center;gap:6px;padding:7px 13px;border-radius:8px;border:1px solid rgba(248,113,113,0.25);background:rgba(248,113,113,0.07);color:var(--red);font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;cursor:pointer;transition:all 0.16s;}
.pm-btn-danger:hover{background:rgba(248,113,113,0.15);}
/* Product grid */
.pm-product-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px;}
.pm-product-card{background:var(--card);border:1px solid var(--border);border-radius:16px;overflow:hidden;transition:transform 0.15s,box-shadow 0.15s;}
.pm-product-card:hover{transform:translateY(-2px);box-shadow:0 8px 32px rgba(0,0,0,0.3);}
/* Modal */
.pm-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:200;display:flex;align-items:flex-start;justify-content:center;padding:24px;overflow-y:auto;backdrop-filter:blur(8px);}
.pm-modal{background:#0d1525;border:1px solid rgba(56,189,248,0.2);border-radius:20px;padding:28px;width:100%;max-width:620px;margin:auto;}
.pm-modal-title{font-family:'Syne',sans-serif;font-size:1.1rem;font-weight:800;color:var(--text);margin-bottom:20px;}
/* Table */
.pm-table{width:100%;border-collapse:collapse;font-size:12px;}
.pm-table th{padding:9px 13px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:var(--muted);border-bottom:1px solid var(--border);white-space:nowrap;}
.pm-table td{padding:10px 13px;border-bottom:1px solid rgba(255,255,255,0.04);vertical-align:middle;}
.pm-table tr:last-child td{border-bottom:none;}
.pm-table tr:hover td{background:rgba(255,255,255,0.02);}
/* Pill */
.pm-pill{font-size:10px;font-weight:700;padding:2px 8px;border-radius:99px;text-transform:uppercase;letter-spacing:0.8px;display:inline-block;font-family:'DM Mono',monospace;}
/* Alert */
.pm-alert{padding:10px 14px;border-radius:9px;font-size:13px;margin-bottom:14px;}
.pm-alert.success{background:rgba(52,211,153,0.1);border:1px solid rgba(52,211,153,0.25);color:var(--green);}
.pm-alert.error{background:rgba(248,113,113,0.1);border:1px solid rgba(248,113,113,0.25);color:var(--red);}
/* Overlay */
.pm-mob-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:90;}
@media(max-width:800px){.pm-mob-overlay.show{display:block;}}
::-webkit-scrollbar{width:4px;height:4px;}::-webkit-scrollbar-track{background:transparent;}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:99px;}
`;

const TYPE_ICONS  = { card:'💳',wallet:'👜',keyring:'🔑',ring:'💍',stand:'🪧',other:'📦' };
const TYPE_COLORS = { card:'#38bdf8',wallet:'#a78bfa',keyring:'#fbbf24',ring:'#34d399',stand:'#f472b6',other:'#4a6080' };
const UNIT_STATUS_COLORS = { unassigned:'#34d399',reserved:'#fbbf24',assigned:'#38bdf8',returned:'#a78bfa',defective:'#f87171' };
const fmtPrice = v => v ? `Rs. ${(v/100).toFixed(0)}` : '—';
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
const Pill = ({label,color='#4a6080'}) => (
  <span className="pm-pill" style={{background:`${color}22`,color}}>{label}</span>
);

function FieldGroup({label,children}) {
  return (
    <div style={{marginBottom:14}}>
      <label style={{display:'block',fontSize:10,fontWeight:700,color:'#4a6080',textTransform:'uppercase',letterSpacing:1.5,marginBottom:5,fontFamily:'DM Mono,monospace'}}>{label}</label>
      {children}
    </div>
  );
}

// ── Product Modal (create / edit) ─────────────────────────────────────────
function ProductModal({product, onClose, onSaved}) {
  const isNew = product === 'new';
  const [form, setForm] = useState(isNew ? {
    name:'', type:'card', description:'', basePrice:'',
    isActive:true, isFeatured:false, tags:'', variants:[]
  } : {
    ...product,
    basePrice: product.basePrice ? String(product.basePrice/100) : '',
    tags: (product.tags||[]).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [alert, setAlert]   = useState(null);

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const addVariant = () => setForm(p=>({...p,variants:[...p.variants,{name:'',price:'',sku:'',imageUrl:''}]}));
  const setVar = (i,k,v) => { const a=[...form.variants]; a[i]={...a[i],[k]:v}; setForm(p=>({...p,variants:a})); };
  const removeVar = i => setForm(p=>({...p,variants:p.variants.filter((_,x)=>x!==i)}));

  const submit = async () => {
    if (!form.name?.trim()) return setAlert({type:'error',msg:'Product name is required.'});
    setSaving(true);
    try {
      const payload = {
        ...form,
        basePrice: form.basePrice ? Math.round(parseFloat(form.basePrice)*100) : 0,
        tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
        variants: form.variants.map(v=>({
          ...v, price: v.price ? Math.round(parseFloat(v.price)*100) : undefined,
        })),
      };
      if (isNew) { await api.post('/admin/products', payload); }
      else       { await api.put(`/admin/products/${product._id}`, payload); }
      onSaved(); onClose();
    } catch(e) { setAlert({type:'error',msg:e.response?.data?.error||'Save failed'}); }
    setSaving(false);
  };

  return (
    <div className="pm-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="pm-modal">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div className="pm-modal-title">{isNew?'New Product':'Edit Product'}</div>
          <button onClick={onClose} style={{background:'transparent',border:'none',color:'#4a6080',cursor:'pointer',fontSize:20}}>✕</button>
        </div>
        {alert && <div className={`pm-alert ${alert.type}`}>{alert.msg}</div>}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <FieldGroup label="Product Name *">
            <input className="pm-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Flap Business Card"/>
          </FieldGroup>
          <FieldGroup label="Type">
            <select className="pm-select" value={form.type} onChange={e=>set('type',e.target.value)}>
              {['card','wallet','keyring','ring','stand','other'].map(t=><option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>)}
            </select>
          </FieldGroup>
          <div style={{gridColumn:'1/-1'}}>
            <FieldGroup label="Description">
              <textarea className="pm-input" style={{resize:'vertical',minHeight:70}} value={form.description||''} onChange={e=>set('description',e.target.value)} placeholder="Short product description…"/>
            </FieldGroup>
          </div>
          <FieldGroup label="Base Price (Rs.)">
            <input className="pm-input" type="number" value={form.basePrice} onChange={e=>set('basePrice',e.target.value)} placeholder="e.g. 999"/>
          </FieldGroup>
          <FieldGroup label="Tags (comma-separated)">
            <input className="pm-input" value={form.tags||''} onChange={e=>set('tags',e.target.value)} placeholder="nfc, card, premium"/>
          </FieldGroup>
          <FieldGroup label="Image URL">
            <input className="pm-input" value={form.imageUrl||''} onChange={e=>set('imageUrl',e.target.value)} placeholder="https://..."/>
          </FieldGroup>
          <div style={{display:'flex',gap:16,alignItems:'center',paddingTop:4}}>
            {[['Active','isActive'],['Featured','isFeatured']].map(([lbl,k])=>(
              <label key={k} style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',fontSize:13,color:'#94a3b8'}}>
                <input type="checkbox" checked={!!form[k]} onChange={e=>set(k,e.target.checked)}
                  style={{width:15,height:15,accentColor:'#38bdf8'}}/>
                {lbl}
              </label>
            ))}
          </div>
        </div>

        {/* Variants */}
        <div style={{marginTop:20,paddingTop:18,borderTop:'1px solid rgba(255,255,255,0.07)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:1.5,fontFamily:'DM Mono,monospace'}}>Variants ({form.variants.length})</div>
            <button className="pm-btn-ghost" onClick={addVariant} style={{fontSize:11}}>+ Add Variant</button>
          </div>
          {form.variants.map((v,i)=>(
            <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr auto',gap:8,marginBottom:8,alignItems:'center'}}>
              <input className="pm-input" value={v.name||''} onChange={e=>setVar(i,'name',e.target.value)} placeholder="Variant name"/>
              <input className="pm-input" type="number" value={v.price||''} onChange={e=>setVar(i,'price',e.target.value)} placeholder="Price Rs."/>
              <input className="pm-input" value={v.sku||''} onChange={e=>setVar(i,'sku',e.target.value)} placeholder="SKU"/>
              <button className="pm-btn-danger" style={{padding:'7px',borderRadius:8}} onClick={()=>removeVar(i)}>✕</button>
            </div>
          ))}
          {form.variants.length===0&&<div style={{fontSize:12,color:'#1a2840',padding:'8px 0'}}>No variants — all units share the base price.</div>}
        </div>

        <div style={{display:'flex',gap:10,marginTop:22,justifyContent:'flex-end'}}>
          <button className="pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="pm-btn-primary" onClick={submit} disabled={saving}>
            {saving?'⏳ Saving…':isNew?'Create Product':'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Units Panel modal ─────────────────────────────────────────────────────
function UnitsPanel({product, onClose}) {
  const [units,   setUnits]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('');
  const [statusF, setStatusF] = useState('');
  const [bulkForm,setBulkForm]= useState({count:'',prefix:'FLP-',variantId:'',batchCode:''});
  const [adding,  setAdding]  = useState(false);
  const [alert,   setAlert]   = useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    try{ const {data}=await api.get(`/admin/products/${product._id}/units`); setUnits(data.units||[]); }
    catch{ setUnits([]); }
    setLoading(false);
  },[product._id]);
  useEffect(()=>{load();},[load]);

  const bulkAdd = async()=>{
    const count = parseInt(bulkForm.count);
    if(!count||count<1||count>500) return setAlert({type:'error',msg:'Enter a count between 1 and 500.'});
    setAdding(true);
    try{
      await api.post(`/admin/products/${product._id}/units/bulk`,{
        count, flapidPrefix:bulkForm.prefix||'FLP-',
        variantId:bulkForm.variantId||undefined,
        batchCode:bulkForm.batchCode||undefined,
      });
      setAlert({type:'success',msg:`${count} units added!`});
      setBulkForm(p=>({...p,count:'',batchCode:''}));
      load();
    } catch(e){ setAlert({type:'error',msg:e.response?.data?.error||'Bulk add failed'}); }
    setAdding(false);
  };

  const updateUnit = async(unitId, status)=>{
    try{ await api.patch(`/admin/products/${product._id}/units/${unitId}`,{status}); load(); }
    catch(e){ alert(e.response?.data?.error||'Update failed'); }
  };

  const filtered = units.filter(u=>{
    const q = filter.toLowerCase();
    const matchQ = !q||u.flapid?.toLowerCase().includes(q)||u.uid?.toLowerCase().includes(q)||u.batchCode?.toLowerCase().includes(q);
    const matchS = !statusF||u.status===statusF;
    return matchQ&&matchS;
  });

  const counts = units.reduce((acc,u)=>{ acc[u.status]=(acc[u.status]||0)+1; return acc; },{});

  return (
    <div className="pm-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="pm-modal" style={{maxWidth:820}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div>
            <div className="pm-modal-title" style={{marginBottom:0}}>{TYPE_ICONS[product.type]} {product.name} — Units</div>
            <div style={{fontSize:11,color:'#4a6080',fontFamily:'DM Mono,monospace',marginTop:2}}>{units.length} total units</div>
          </div>
          <button onClick={onClose} style={{background:'transparent',border:'none',color:'#4a6080',cursor:'pointer',fontSize:20}}>✕</button>
        </div>

        {/* Stock summary */}
        <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
          {Object.entries(UNIT_STATUS_COLORS).map(([s,c])=>(
            <div key={s} style={{padding:'7px 14px',borderRadius:10,background:`${c}14`,border:`1px solid ${c}33`,textAlign:'center',minWidth:80}}>
              <div style={{fontSize:18,fontWeight:800,color:c,fontFamily:'DM Mono,monospace'}}>{counts[s]||0}</div>
              <div style={{fontSize:9,color:'#4a6080',textTransform:'uppercase',letterSpacing:1}}>{s}</div>
            </div>
          ))}
        </div>

        {alert && <div className={`pm-alert ${alert.type}`} style={{marginBottom:14}}>{alert.msg}</div>}

        {/* Bulk add */}
        <div style={{background:'rgba(56,189,248,0.05)',border:'1px solid rgba(56,189,248,0.15)',borderRadius:12,padding:'14px 16px',marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:'#38bdf8',textTransform:'uppercase',letterSpacing:1.5,marginBottom:10,fontFamily:'DM Mono,monospace'}}>Bulk Add Units</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:8,alignItems:'end'}}>
            <FieldGroup label="Count"><input className="pm-input" type="number" value={bulkForm.count} onChange={e=>setBulkForm(p=>({...p,count:e.target.value}))} placeholder="e.g. 50"/></FieldGroup>
            <FieldGroup label="FlapID Prefix"><input className="pm-input" value={bulkForm.prefix} onChange={e=>setBulkForm(p=>({...p,prefix:e.target.value}))} placeholder="FLP-"/></FieldGroup>
            <FieldGroup label="Batch Code"><input className="pm-input" value={bulkForm.batchCode} onChange={e=>setBulkForm(p=>({...p,batchCode:e.target.value}))} placeholder="BATCH-001"/></FieldGroup>
            {product.variants?.length>0&&(
              <FieldGroup label="Variant">
                <select className="pm-select" value={bulkForm.variantId} onChange={e=>setBulkForm(p=>({...p,variantId:e.target.value}))}>
                  <option value="">— Base —</option>
                  {product.variants.map(v=><option key={v._id} value={v._id}>{v.name}</option>)}
                </select>
              </FieldGroup>
            )}
            <button className="pm-btn-primary" onClick={bulkAdd} disabled={adding} style={{marginBottom:14}}>
              {adding?'Adding…':'+ Add'}
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          <input className="pm-input" style={{flex:1}} value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Search flapid, uid, batch…"/>
          <select className="pm-select" style={{width:'auto'}} value={statusF} onChange={e=>setStatusF(e.target.value)}>
            <option value="">All Status</option>
            {Object.keys(UNIT_STATUS_COLORS).map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Units table */}
        {loading ? <div style={{textAlign:'center',color:'#4a6080',padding:30}}>Loading…</div> : (
          <div style={{overflowX:'auto',maxHeight:340,overflowY:'auto'}}>
            <table className="pm-table">
              <thead>
                <tr>
                  <th>FlapID</th><th>UID</th><th>Status</th>
                  <th>Variant</th><th>Batch</th><th>Added</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length===0&&(
                  <tr><td colSpan={7} style={{textAlign:'center',color:'#4a6080',padding:24}}>No units match filter</td></tr>
                )}
                {filtered.map(u=>(
                  <tr key={u._id}>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:11,color:'#38bdf8'}}>{u.flapid||'—'}</td>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:10,color:'#4a6080'}}>{u.uid||'—'}</td>
                    <td><Pill label={u.status} color={UNIT_STATUS_COLORS[u.status]}/></td>
                    <td style={{fontSize:11,color:'#94a3b8'}}>{u.variantName||'—'}</td>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:10,color:'#4a6080'}}>{u.batchCode||'—'}</td>
                    <td style={{fontSize:11,color:'#4a6080'}}>{fmtDate(u.addedAt)}</td>
                    <td>
                      <select style={{background:'rgba(13,21,37,0.9)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:6,color:'#94a3b8',fontSize:11,padding:'3px 6px',cursor:'pointer'}}
                        value={u.status} onChange={e=>updateUnit(u._id,e.target.value)}>
                        {Object.keys(UNIT_STATUS_COLORS).map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:16}}>
          <button className="pm-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Import Tab ───────────────────────────────────────────────────────
function BulkImportTab({products}) {
  const [productId, setProductId] = useState('');
  const [csv,       setCsv]       = useState('');
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);

  const submit = async () => {
    if (!productId) return alert('Select a product first.');
    if (!csv.trim()) return alert('Paste CSV data first.');
    setLoading(true);
    try {
      const lines = csv.trim().split('\n').filter(l=>l.trim());
      const units = lines.map(l=>{
        const [flapid,uid,batchCode,variantName] = l.split(',').map(s=>s.trim());
        return { flapid, uid:uid||undefined, batchCode:batchCode||undefined, variantName:variantName||undefined };
      });
      const {data} = await api.post(`/admin/products/${productId}/units/bulk`,{ units });
      setResult({type:'success',msg:`Added ${data.added||units.length} units.`});
      setCsv('');
    } catch(e){ setResult({type:'error',msg:e.response?.data?.error||'Import failed'}); }
    setLoading(false);
  };

  return (
    <div style={{maxWidth:700}}>
      <div style={{background:'rgba(56,189,248,0.05)',border:'1px solid rgba(56,189,248,0.12)',borderRadius:14,padding:20,marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:'#38bdf8',marginBottom:8}}>📋 CSV Format</div>
        <div style={{fontFamily:'DM Mono,monospace',fontSize:11,color:'#4a6080',lineHeight:1.8}}>
          flapid, uid, batchCode, variantName<br/>
          FLP-000001, 04:A1:B2:C3, BATCH-001, Premium<br/>
          FLP-000002, 04:D4:E5:F6, BATCH-001,<br/>
          FLP-000003,,BATCH-002,Standard
        </div>
      </div>

      {result && <div className={`pm-alert ${result.type}`} style={{marginBottom:16}}>{result.msg}</div>}

      <FieldGroup label="Select Product">
        <select className="pm-select" value={productId} onChange={e=>setProductId(e.target.value)}>
          <option value="">— Choose product —</option>
          {products.map(p=><option key={p._id} value={p._id}>{TYPE_ICONS[p.type]} {p.name}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup label="CSV Data (paste or type)">
        <textarea className="pm-input" style={{minHeight:220,resize:'vertical',fontFamily:'DM Mono,monospace',fontSize:12}}
          value={csv} onChange={e=>setCsv(e.target.value)}
          placeholder="flapid, uid, batchCode, variantName&#10;FLP-000001, 04:A1:B2:C3, BATCH-001, Premium"/>
      </FieldGroup>

      <div style={{display:'flex',gap:10,alignItems:'center'}}>
        <button className="pm-btn-primary" onClick={submit} disabled={loading||!productId||!csv.trim()}>
          {loading?'⏳ Importing…':'📥 Import Units'}
        </button>
        <div style={{fontSize:12,color:'#4a6080'}}>
          {csv.trim() ? `${csv.trim().split('\n').filter(l=>l.trim()).length} rows` : 'No data yet'}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar nav config ────────────────────────────────────────────────────
const SIDE_NAV = [
  { title:'Products', items:[
    { icon:'📦', label:'All Products', tab:'products' },
    { icon:'💳', label:'Unit Inventory', tab:'units'    },
    { icon:'📥', label:'Bulk Import',   tab:'import'   },
  ]},
  { title:'Platform', items:[
    { icon:'🛡', label:'Admin Panel', path:'/admin'     },
    { icon:'📊', label:'Dashboard',   path:'/dashboard' },
    { icon:'👤', label:'Profile',     path:'/profile'   },
  ]},
];

const TABS = [
  {key:'products', label:'📦 Products'},
  {key:'units',    label:'💳 All Units'},
  {key:'import',   label:'📥 Bulk Import'},
];

export default function ProductsManager() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [tab,      setTab]      = useState('products');
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [unitsProd,setUnitsProd]= useState(null);
  const [search,   setSearch]   = useState('');
  const [typeF,    setTypeF]    = useState('');
  const [col,      setCol]      = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  // All units tab
  const [allUnits, setAllUnits] = useState([]);
  const [unitsLoad,setUnitsLoad]= useState(false);
  const [uFilter,  setUFilter]  = useState('');
  const [uStatus,  setUStatus]  = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    try {
      const p = JSON.parse(atob(token.split('.')[1]));
      if (!['admin','superadmin'].includes(p.role)) navigate('/dashboard');
    } catch { navigate('/login'); }
  }, [navigate]);

  const loadProducts = useCallback(async()=>{
    setLoading(true);
    try{ const {data}=await api.get('/admin/products'); setProducts(data.products||[]); }
    catch{ try{ const {data}=await api.get('/products'); setProducts(data||[]); } catch{} }
    setLoading(false);
  },[]);

  const loadAllUnits = useCallback(async()=>{
    if(allUnits.length>0) return;
    setUnitsLoad(true);
    try{
      const results = await Promise.all(
        products.map(p=>api.get(`/admin/products/${p._id}/units`).then(r=>
          (r.data.units||[]).map(u=>({...u,productName:p.name,productType:p.type}))
        ).catch(()=>[]))
      );
      setAllUnits(results.flat());
    }catch{}
    setUnitsLoad(false);
  },[products, allUnits.length]);

  useEffect(()=>{ loadProducts(); },[loadProducts]);
  useEffect(()=>{ if(tab==='units') loadAllUnits(); },[tab, loadAllUnits]);

  const logout = ()=>{ localStorage.removeItem('token'); navigate('/login'); };
  const deleteProduct = async(id)=>{
    if(!window.confirm('Delete this product?')) return;
    try{ await api.delete(`/products/${id}`); loadProducts(); }
    catch(e){ alert(e.response?.data?.error||'Delete failed'); }
  };

  const filtered = products.filter(p=>{
    const q=search.toLowerCase();
    return (!q||p.name?.toLowerCase().includes(q)||(p.tags||[]).join(' ').includes(q))
      && (!typeF||p.type===typeF);
  });

  const filteredUnits = allUnits.filter(u=>{
    const q=uFilter.toLowerCase();
    return (!q||u.flapid?.toLowerCase().includes(q)||u.uid?.toLowerCase().includes(q)||u.productName?.toLowerCase().includes(q))
      && (!uStatus||u.status===uStatus);
  });

  // ── Sidebar ──
  const Sidebar = () => (
    <nav className={`pm-sidebar ${col?'col':''} ${!sideOpen&&window.innerWidth<=800?'hidden':''}`}>
      <div className="pm-sb-head">
        {!col&&<a href="/" className="pm-logo">flap<span>.</span></a>}
        <button className="pm-col-btn" onClick={()=>{setCol(c=>!c);setSideOpen(false);}}>
          {col?'→':'←'}
        </button>
      </div>
      {SIDE_NAV.map(sec=>(
        <div className="pm-sb-section" key={sec.title}>
          <span className="pm-sb-sec-title">{sec.title}</span>
          {sec.items.map(item=>{
            const isActive = item.tab ? tab===item.tab : location.pathname===item.path;
            return (
              <button key={item.tab||item.path} className={`pm-nav-item ${isActive?'active':''}`}
                onClick={()=>{ if(item.tab){setTab(item.tab);setSideOpen(false);} else navigate(item.path); }}>
                <span className="pm-nav-icon">{item.icon}</span>
                <span className="pm-nav-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      ))}
      <div className="pm-sb-bot">
        <button className="pm-logout" onClick={logout}>
          <span style={{fontSize:14}}>⏻</span>
          <span className="pm-logout-lbl">Log Out</span>
        </button>
      </div>
    </nav>
  );

  return (
    <div className="pm-root">
      <style>{CSS}</style>
      <Sidebar/>
      <div className={`pm-mob-overlay ${sideOpen?'show':''}`} onClick={()=>setSideOpen(false)}/>

      <div className="pm-main">
        {/* Mobile topbar */}
        <div className="pm-topbar">
          <button className="pm-menu-btn" onClick={()=>setSideOpen(s=>!s)}>☰</button>
          <a href="/" className="pm-logo" style={{textDecoration:'none'}}>flap<span style={{color:'var(--gold)'}}>.</span></a>
          <button className="pm-btn-primary" style={{padding:'6px 14px',fontSize:12}} onClick={()=>setModal('new')}>+ New</button>
        </div>

        {/* Header */}
        <div className="pm-header">
          <div>
            <div className="pm-title">📦 Products & Inventory</div>
            <div className="pm-sub">{products.length} products · Flap Admin</div>
          </div>
          {tab==='products'&&(
            <button className="pm-btn-primary" onClick={()=>setModal('new')}>+ New Product</button>
          )}
        </div>

        {/* Tabs */}
        <div className="pm-tabs">
          {TABS.map(t=>(
            <button key={t.key} className={`pm-tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        <div className="pm-content">

          {/* ── PRODUCTS TAB ── */}
          {tab==='products'&&(
            <>
              {/* Filters */}
              <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
                <input className="pm-input" style={{flex:1,minWidth:200}} value={search}
                  onChange={e=>setSearch(e.target.value)} placeholder="Search products…"/>
                <select className="pm-select" style={{width:'auto'}} value={typeF} onChange={e=>setTypeF(e.target.value)}>
                  <option value="">All Types</option>
                  {['card','wallet','keyring','ring','stand','other'].map(t=>(
                    <option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>
                  ))}
                </select>
              </div>

              {loading&&<div style={{textAlign:'center',color:'#4a6080',padding:'60px 0'}}>Loading products…</div>}

              {!loading&&filtered.length===0&&(
                <div style={{textAlign:'center',padding:'80px 20px'}}>
                  <div style={{fontSize:48,marginBottom:12}}>📦</div>
                  <div style={{fontSize:18,fontWeight:700,color:'#e2e8f0',fontFamily:'Syne,sans-serif',marginBottom:8}}>No products yet</div>
                  <div style={{fontSize:13,color:'#4a6080',marginBottom:20}}>Create your first product to start managing inventory</div>
                  <button className="pm-btn-primary" onClick={()=>setModal('new')}>+ Create Product</button>
                </div>
              )}

              <div className="pm-product-grid">
                {filtered.map(p=>{
                  const sc=p.stockCounts||{};
                  const typeColor=TYPE_COLORS[p.type]||'#4a6080';
                  return (
                    <div key={p._id} className="pm-product-card">
                      <div style={{height:4,background:`linear-gradient(90deg,${typeColor},${typeColor}55)`}}/>
                      <div style={{padding:'18px 20px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <span style={{fontSize:22}}>{TYPE_ICONS[p.type]}</span>
                            <div>
                              <div style={{fontSize:14,fontWeight:700,color:'#e2e8f0',fontFamily:'Syne,sans-serif'}}>{p.name}</div>
                              <div style={{display:'flex',gap:5,marginTop:3,flexWrap:'wrap'}}>
                                <Pill label={p.type} color={typeColor}/>
                                {!p.isActive&&<Pill label="inactive" color="#f87171"/>}
                                {p.isFeatured&&<Pill label="featured" color="#fbbf24"/>}
                              </div>
                            </div>
                          </div>
                          <div style={{fontSize:15,fontWeight:800,color:'#34d399',fontFamily:'DM Mono,monospace',flexShrink:0}}>{fmtPrice(p.basePrice)}</div>
                        </div>
                        {p.description&&<div style={{fontSize:11,color:'#4a6080',marginBottom:10,lineHeight:1.5,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{p.description}</div>}
                        {sc.total>0?(
                          <div style={{display:'flex',gap:10,marginBottom:12,padding:'8px 12px',background:'rgba(255,255,255,0.02)',borderRadius:9,flexWrap:'wrap'}}>
                            {[['Available',sc.unassigned||0,'#34d399'],['Assigned',sc.assigned||0,'#38bdf8'],['Defective',sc.defective||0,'#f87171'],['Total',sc.total,'#4a6080']].map(([l,v,c])=>(
                              <div key={l} style={{textAlign:'center'}}>
                                <div style={{fontSize:15,fontWeight:800,color:c,fontFamily:'DM Mono,monospace'}}>{v}</div>
                                <div style={{fontSize:9,color:'#4a6080',textTransform:'uppercase',letterSpacing:0.8}}>{l}</div>
                              </div>
                            ))}
                          </div>
                        ):(
                          <div style={{fontSize:11,color:'#1a2840',marginBottom:12,padding:'6px 10px',borderRadius:7,border:'1px dashed rgba(255,255,255,0.07)'}}>No units yet</div>
                        )}
                        <div style={{display:'flex',gap:8}}>
                          <button className="pm-btn-ghost" style={{flex:1,fontSize:12,color:'#38bdf8',borderColor:'rgba(56,189,248,0.25)'}} onClick={()=>setUnitsProd(p)}>💳 {sc.total||0} Units</button>
                          <button className="pm-btn-ghost" style={{flex:1,fontSize:12}} onClick={()=>setModal(p)}>✏️ Edit</button>
                          <button className="pm-btn-danger" style={{padding:'7px 12px'}} onClick={()=>deleteProduct(p._id)}>🗑</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── ALL UNITS TAB ── */}
          {tab==='units'&&(
            <>
              <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
                <input className="pm-input" style={{flex:1,minWidth:200}} value={uFilter} onChange={e=>{setUFilter(e.target.value);}} placeholder="Search flapid, uid, product…"/>
                <select className="pm-select" style={{width:'auto'}} value={uStatus} onChange={e=>setUStatus(e.target.value)}>
                  <option value="">All Status</option>
                  {Object.keys(UNIT_STATUS_COLORS).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <button className="pm-btn-ghost" onClick={()=>{setAllUnits([]); loadAllUnits();}}>↻ Refresh</button>
              </div>
              {unitsLoad&&<div style={{textAlign:'center',color:'#4a6080',padding:40}}>Loading units…</div>}
              {!unitsLoad&&(
                <div style={{overflowX:'auto'}}>
                  <table className="pm-table">
                    <thead>
                      <tr><th>FlapID</th><th>UID</th><th>Product</th><th>Status</th><th>Variant</th><th>Batch</th><th>Added</th></tr>
                    </thead>
                    <tbody>
                      {filteredUnits.length===0&&<tr><td colSpan={7} style={{textAlign:'center',color:'#4a6080',padding:32}}>No units found</td></tr>}
                      {filteredUnits.map((u,i)=>(
                        <tr key={u._id||i}>
                          <td style={{fontFamily:'DM Mono,monospace',fontSize:11,color:'#38bdf8'}}>{u.flapid||'—'}</td>
                          <td style={{fontFamily:'DM Mono,monospace',fontSize:10,color:'#4a6080'}}>{u.uid||'—'}</td>
                          <td><div style={{fontSize:12}}>{TYPE_ICONS[u.productType]} {u.productName}</div></td>
                          <td><Pill label={u.status} color={UNIT_STATUS_COLORS[u.status]}/></td>
                          <td style={{fontSize:11,color:'#94a3b8'}}>{u.variantName||'—'}</td>
                          <td style={{fontFamily:'DM Mono,monospace',fontSize:10,color:'#4a6080'}}>{u.batchCode||'—'}</td>
                          <td style={{fontSize:11,color:'#4a6080'}}>{fmtDate(u.addedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{textAlign:'center',padding:'10px 0',fontSize:11,color:'#4a6080'}}>
                    {filteredUnits.length} of {allUnits.length} units
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── BULK IMPORT TAB ── */}
          {tab==='import'&&<BulkImportTab products={products}/>}

        </div>
      </div>

      {modal&&<ProductModal product={modal} onClose={()=>setModal(null)} onSaved={loadProducts}/>}
      {unitsProd&&<UnitsPanel product={unitsProd} onClose={()=>{setUnitsProd(null);loadProducts();setAllUnits([]);}}/>}
    </div>
  );
}